import { Box, Stack } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { NavLink } from 'react-router-dom';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

export type SubNavItem = {
  to: string;
  label: string;
  icon?: ReactNode;
  end?: boolean;
};

export function SubNav({ items }: { items: SubNavItem[] }): JSX.Element {
  const theme = useTheme();
  const { t } = useTranslation();
  return (
    <Box
      component="nav"
      aria-label={t('nav.subNavAriaLabel')}
      sx={{
        position: 'sticky',
        top: { xs: 60, md: 92 },
        zIndex: theme.zIndex.appBar - 1,
        backgroundColor: theme.palette.background.paper,
        borderBottom: `1px solid ${theme.palette.divider}`,
        mb: 3,
      }}
    >
      <Box
        sx={{
          maxWidth: 1440,
          mx: 'auto',
          px: { xs: 2, md: 3 },
        }}
      >
        <Stack
          direction="row"
          spacing={0}
          sx={{
            overflowX: 'auto',
            '&::-webkit-scrollbar': { display: 'none' },
            scrollbarWidth: 'none',
          }}
        >
          {items.map((item) => (
            <Box
              key={item.to}
              component={NavLink}
              to={item.to}
              {...(item.end ? { end: true } : {})}
              sx={({ palette }) => ({
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.625,
                position: 'relative',
                px: 1.75,
                py: 1.5,
                fontSize: '0.875rem',
                fontWeight: 500,
                color: palette.text.secondary,
                textDecoration: 'none',
                whiteSpace: 'nowrap',
                transition: 'color 140ms ease',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  left: 14,
                  right: 14,
                  bottom: -1,
                  height: 2,
                  borderRadius: '2px 2px 0 0',
                  backgroundColor: 'transparent',
                  transition: 'background-color 140ms ease',
                },
                '&:hover': {
                  color: palette.text.primary,
                  '&::after': { backgroundColor: palette.divider },
                },
                '&.active': {
                  color: palette.text.primary,
                  fontWeight: 600,
                  '&::after': { backgroundColor: palette.primary.main },
                },
              })}
            >
              {item.icon}
              {item.label}
            </Box>
          ))}
        </Stack>
      </Box>
    </Box>
  );
}
